// Format currency
export function formatCurrency(amount, currency = 'USD') {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
  }).format(amount)
}

// Format date
export function formatDate(date, options = {}) {
  if (!date) return 'N/A'
  const dateObj = new Date(date)
  if (isNaN(dateObj.getTime())) return 'Invalid Date'
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    ...options,
  }).format(dateObj)
}

// Format date with time
export function formatDateTime(date) {
  if (!date) return 'N/A'
  const dateObj = new Date(date)
  // Check if date is valid
  if (isNaN(dateObj.getTime())) return 'Invalid Date'
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(dateObj)
}

// Format duration in seconds to mm:ss
export function formatDuration(seconds) {
  if (!seconds) return '0:00'
  const mins = Math.floor(seconds / 60)
  const secs = seconds % 60
  return `${mins}:${secs.toString().padStart(2, '0')}`
}

// Format relative time (e.g., "2 hours ago")
export function formatRelativeTime(date) {
  if (!date) return 'N/A'
  const now = new Date()
  const then = new Date(date)
  if (isNaN(then.getTime())) return 'Invalid Date'
  const diffMs = now - then
  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMs / 3600000)
  const diffDays = Math.floor(diffMs / 86400000)

  if (diffMins < 1) return 'just now'
  if (diffMins < 60) return `${diffMins}m ago`
  if (diffHours < 24) return `${diffHours}h ago`
  if (diffDays < 7) return `${diffDays}d ago`
  return formatDate(date)
}

// Dispute reason labels
export const disputeReasonLabels = {
  wrong_number: 'Wrong Number',
  duplicate: 'Duplicate Lead',
  not_interested: 'Not Interested',
  no_answer: 'No Answer',
  language_barrier: 'Language Barrier',
  other: 'Other',
}

// Status badge colors
export const statusColors = {
  new: 'bg-info/20 text-info',
  converted: 'bg-accent/20 text-accent',
  disputed: 'bg-warning/20 text-warning',
  pending: 'bg-warning/20 text-warning',
  approved: 'bg-accent/20 text-accent',
  rejected: 'bg-danger/20 text-danger',
  active: 'bg-accent/20 text-accent',
  inactive: 'bg-[#fafafa]/20 text-[#b0b0b0]',
  confirmed: 'bg-accent/20 text-accent',
  failed: 'bg-danger/20 text-danger',
}

// Transaction type labels
export const transactionTypeLabels = {
  charge: 'Call Charge',
  credit: 'Credit',
  payment: 'Payment',
  dispute_credit: 'Dispute Credit',
  adjustment: 'Adjustment',
}

// Get initials from name
export function getInitials(name) {
  if (!name) return '?'
  return name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2)
}

// Classnames helper
export function cn(...classes) {
  return classes.filter(Boolean).join(' ')
}

// Get display status for a call based on all available fields
export function getCallDisplayStatus(call) {
  // Priority order: Live > Disputed > Converted > Lost > New
  if (call.is_live) {
    return { status: 'live', label: 'Live', variant: 'info' }
  }
  if (call.status === 'disputed') {
    return { status: 'disputed', label: 'Disputed', variant: 'warning' }
  }
  if (call.status === 'converted' || call.dialics_status?.includes('With Conversion')) {
    return { status: 'converted', label: 'Converted', variant: 'success' }
  }
  if (call.dialics_status === 'Call Not Connected') {
    return { status: 'lost', label: 'Lost', variant: 'danger' }
  }
  // Default to "New" for completed calls without conversion
  return { status: 'new', label: 'New', variant: 'default' }
}

// Call status labels for display
export const callStatusLabels = {
  live: 'Live',
  converted: 'Converted',
  lost: 'Lost',
  disputed: 'Disputed',
  new: 'New',
}

// ========================================
// NUMBER PERFORMANCE UTILITIES
// ========================================

/**
 * Format phone number for display with spacing
 * French: 097 050 1273 (3-3-4)
 * Belgian/longer: 082 045 494 (3-3-3...)
 */
export function formatPhoneNumber(phone, { withPrefix = false, country } = {}) {
  if (!phone) return ''
  const cleaned = phone.replace(/\D/g, '')

  // Country prefix map
  const PREFIX = { FR: '+33', BE: '+32', UK: '+44', DE: '+49', ES: '+34', IT: '+39' }

  // French format (10 digits starting with 0) — show as +33 X XX XX XX XX
  if (cleaned.startsWith('0') && cleaned.length === 10) {
    if (withPrefix) {
      const nat = cleaned.slice(1) // drop leading 0
      return `+33 ${nat.slice(0, 1)} ${nat.slice(1, 3)} ${nat.slice(3, 5)} ${nat.slice(5, 7)} ${nat.slice(7)}`
    }
    return `${cleaned.slice(0, 3)} ${cleaned.slice(3, 6)} ${cleaned.slice(6)}`
  }

  // If country known and withPrefix, always use correct country prefix
  if (withPrefix && country && PREFIX[country]) {
    // Strip leading 0 for national numbers
    const nat = cleaned.startsWith('0') ? cleaned.slice(1) : cleaned
    // Strip country code if already present
    const cc = PREFIX[country].slice(1) // e.g. "33", "44"
    const digits = nat.startsWith(cc) ? nat.slice(cc.length) : nat
    return `${PREFIX[country]} ${digits.replace(/(\d{2,3})(?=\d)/g, '$1 ').trim()}`
  }

  // International format (starts with country code, 11+ digits) — no country hint
  if (cleaned.length >= 11) {
    if (cleaned.startsWith('33')) return `+33 ${cleaned.slice(2, 3)} ${cleaned.slice(3, 5)} ${cleaned.slice(5, 7)} ${cleaned.slice(7, 9)} ${cleaned.slice(9)}`
    if (cleaned.startsWith('32')) return `+32 ${cleaned.slice(2, 5)} ${cleaned.slice(5, 7)} ${cleaned.slice(7)}`
    if (cleaned.startsWith('44')) return `+44 ${cleaned.slice(2, 6)} ${cleaned.slice(6)}`
    return `+${cleaned.slice(0, 2)} ${cleaned.slice(2, 5)} ${cleaned.slice(5, 8)} ${cleaned.slice(8)}`
  }

  // Default: add spaces every 3 digits
  return cleaned.replace(/(\d{3})(?=\d)/g, '$1 ')
}

/**
 * Get efficiency label and color for Click:Call ratio
 * Lower ratio = better efficiency (fewer clicks needed per call)
 */
export function getClickCallEfficiency(ratio) {
  if (ratio === null || ratio === undefined) {
    return { label: '-', color: 'text-[#b0b0b0]', bgColor: 'bg-[#f5f5f5]', level: 'unknown' }
  }

  // Thresholds calibrated for real IP-based unique clicks (~95% unique rate)
  if (ratio <= 40) {
    return { label: 'Excellent', color: 'text-[#329938]', bgColor: 'bg-[#e8f5e9]', level: 'excellent' }
  }
  if (ratio <= 75) {
    return { label: 'Good', color: 'text-[#2b7cd6]', bgColor: 'bg-[#e4eefc]', level: 'good' }
  }
  if (ratio <= 120) {
    return { label: 'Average', color: 'text-[#c8920f]', bgColor: 'bg-[#fdf3d8]', level: 'average' }
  }
  return { label: 'Poor', color: 'text-[#e05252]', bgColor: 'bg-[#fde8e8]', level: 'poor' }
}

/**
 * Get color classes for Conversion Rate percentage
 */
export function getCRColor(cr) {
  if (cr === null || cr === undefined) {
    return { text: 'text-[#b0b0b0]', bg: 'bg-[#f5f5f5]' }
  }

  if (cr >= 70) return { text: 'text-[#329938]', bg: 'bg-[#e8f5e9]' }
  if (cr >= 50) return { text: 'text-[#329938]', bg: 'bg-[#e8f5e9]' }
  if (cr >= 40) return { text: 'text-[#c8920f]', bg: 'bg-[#fdf3d8]' }
  return { text: 'text-[#e05252]', bg: 'bg-[#fde8e8]' }
}

/**
 * Get rank badge for top performers
 */
export function getRankBadge(rank) {
  if (rank === 1) return { emoji: '🥇', bg: 'bg-[#fdf3d8]', text: 'text-[#7a5a0b]' }
  if (rank === 2) return { emoji: '🥈', bg: 'bg-[#ececec]', text: 'text-[#4a4a4a]' }
  if (rank === 3) return { emoji: '🥉', bg: 'bg-[#fdf3d8]', text: 'text-[#7a5a0b]' }
  return { emoji: null, bg: 'bg-[#f5f5f5]', text: 'text-[#8a8a8a]' }
}

/**
 * Determine row highlighting based on performance
 */
export function getRowHighlight(row) {
  const isPerfect = row.conversionRate === 100 && row.conversions >= 3
  const isHighWaste = row.clickToCallRatio > 120
  const isLowCR = row.conversionRate < 40 && row.calls >= 5

  if (isPerfect) return 'bg-[#e8f5e9]/50'
  if (isHighWaste) return 'bg-[#fde8e8]/30'
  if (isLowCR) return 'bg-[#fdf3d8]/30'
  return ''
}

/**
 * Get performance indicators for a phone number
 */
export function getPerformanceIndicators(row) {
  const indicators = []

  if (row.conversionRate === 100 && row.conversions >= 3) {
    indicators.push({ emoji: '🔥', title: 'Perfect 100% CR' })
  }
  if (row.clickToCallRatio > 120) {
    indicators.push({ emoji: '⚠️', title: 'High click waste' })
  }
  if (row.conversionRate >= 70 && row.conversions >= 5) {
    indicators.push({ emoji: '⭐', title: 'Top performer' })
  }

  return indicators
}
